import { apiClient } from '../lib/apiClient';

/**
 * Onboarding tour preference (backend src/routes/meOnboarding.routes.ts):
 *   GET       /api/v1/me/onboarding                         → { data: { showOnStart } }
 *   PUT|PATCH /api/v1/me/onboarding  { showOnStart: boolean } → { data: { showOnStart } }
 *
 * `showOnStart` true: the tour opens once per app session. `seenVersion`: the
 * newest tour version shown to this user (0 = never) — no tracking of which
 * slides were viewed. The client compares it with its slides' versions
 * (components/onboarding/slides.ts) and reports a showing with
 * POST /me/onboarding/seen { version }. `voice`: voice notes are switched on.
 */
export interface OnboardingPreference {
  showOnStart: boolean;
  seenVersion?: number;
  voice?: boolean;
}

/** Anything but a boolean `true` counts as "do not open automatically". */
function parsePreference(body: unknown): OnboardingPreference {
  const data = (body as { data?: { showOnStart?: unknown; seenVersion?: unknown; voice?: unknown } } | null)?.data;
  const seen = data?.seenVersion;
  return {
    showOnStart: data?.showOnStart === true,
    seenVersion: typeof seen === 'number' && Number.isInteger(seen) && seen >= 0 ? seen : 0,
    voice: data?.voice === true,
  };
}

/** The tour up to `version` was shown (opened automatically) — don't show it again. */
export async function markOnboardingSeen(version: number): Promise<void> {
  await apiClient.post('/api/v1/me/onboarding/seen', { version });
}

export async function fetchOnboardingPreference(): Promise<OnboardingPreference> {
  return parsePreference(await apiClient.get<unknown>('/api/v1/me/onboarding'));
}

export async function updateOnboardingPreference(showOnStart: boolean): Promise<OnboardingPreference> {
  const body = await apiClient.patch<unknown>('/api/v1/me/onboarding', { showOnStart });
  const data = (body as { data?: { showOnStart?: unknown } } | null)?.data;
  return { showOnStart: typeof data?.showOnStart === 'boolean' ? data.showOnStart : showOnStart };
}
